import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError, filter, map, startWith, switchMap } from 'rxjs/operators';
import { defaultImagePlaceholder } from '../image';
import { MiqSecuredResourcesService } from '../miq-secure-resources/miq-secured-resources.service';
import { ImageContainerBGColor, SecureImage } from './bg-image.models';
import { NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line
  selector: '[miq-bg-image]',
  template: `
    <div class="image-icons">
      @if (showImage() && !effectivePublishable() && !isPDF()) {
        <mat-icon
          class="no-publish-icon"
          [matTooltip]="nonPublishableTooltip()"
          [ngClass]="customPublishIconClass()"
          svgIcon="icon-no-publish"
        ></mat-icon>
      }
      <ng-content></ng-content>
    </div>
    @if (showImage()) {
      <div class="image-container" [ngClass]="color()" [ngStyle]="backgroundImage$ | async"></div>
    }
    `,
  styleUrl: './bg-image.component.scss',
  imports: [MatIcon, MatTooltip, NgClass, NgStyle, AsyncPipe],
})
export class BgImageComponent {
  private readonly securedResourceResolver = inject(MiqSecuredResourcesService);

  readonly defaultImagePath = input(defaultImagePlaceholder);
  readonly imageSrc = input<string>();
  readonly publishable = input(true);
  readonly nonPublishableTooltip = input<string>();
  readonly color = input<ImageContainerBGColor>('default');
  readonly customPublishIconClass = input('');
  readonly showImage = input(true);
  readonly secureImage = input<SecureImage>();

  readonly effectiveImageSrc = computed(() => {
    const secure = this.secureImage();
    if (secure != null) {
      return typeof secure.imageSrc === 'string' ? (secure.imageSrc as string) : null;
    }
    return this.imageSrc();
  });

  readonly effectivePublishable = computed(() => {
    const secure = this.secureImage();
    return secure != null ? secure.publishable : this.publishable();
  });

  readonly isPDF = computed(() => {
    const secure = this.secureImage();
    return secure != null ? secure.isPDF : false;
  });

  backgroundImage$ = toObservable(this.effectiveImageSrc).pipe(
    startWith(this.defaultImagePath()),
    filter(url => !!url),
    switchMap(url => this.loadImage(url).pipe(catchError(() => of(this.defaultImagePath())))),
    map(url => this.getBackgroundImageStyle(url))
  );

  loadImage(imageSrc) {
    if (imageSrc === this.defaultImagePath()) {
      return of(imageSrc);
    }
    return this.securedResourceResolver.loadImage(imageSrc);
  }

  public getBackgroundImageStyle(imageSrc) {
    if (imageSrc === this.defaultImagePath()) {
      // for svg coloring
      return {
        mask: `url(${imageSrc})`,
        '-webkit-mask': `url(${imageSrc})`,
        '-webkit-mask-size': 'cover',
      };
    }
    return {
      'background-image': `url(${imageSrc})`,
      'background-position': 'center center',
      'background-size': 'contain',
      'background-repeat': 'no-repeat',
    };
  }
}
